import React, { useEffect, useState } from 'react'
import { Container, HtmlContent, ToastContext } from '@pmi/pmi-iqos-ds-react'
import {
  cookie,
  setTranslations,
  CommonTranslations,
  extractErrorMessage,
  mapProductInfoForDisplay,
  parseTranslations,
  isProductEligibleToShow,
  PublishedComponent
} from '@pmi/pmi-iqos-shared-react'
import { ProductBanner, ProductDetailView } from '@dcx-common/components'
import { DcxApiManager } from '@dcx-common/services'
import ProductDetailTranslations from './product-detail.json'

const ProductDetailComponent = ({
  translations,
  productData,
  crossSellBanner,
  showbar,
  hidereviews,
  backgroundColor,
  bundleDetailPath,
  canonicalUrl,
  pdpCtaNdr,
  line
}) => {
  const [productDataObject, setProductDataObject] = useState(productData)
  const [hideProduct, setHideProduct] = useState(false)
  const [pageReady, setPageReady] = useState(false)
  const api = new DcxApiManager(line)

  useEffect(() => {
    api.setErrorTranslations(translations)
    checkHideProduct()
    setPageReady(true)
  }, [])

  const checkHideProduct = () => {
    if (!isProductEligibleToShow(productDataObject.product, cookie)) {
      setHideProduct(true)
    }
  }

  const updateDataProduct = updatedDataProduct => {
    setProductDataObject({
      ...productDataObject,
      product: {
        ...productDataObject.product,
        ...updatedDataProduct
      }
    })
  }

  const updateData = updatedData => {
    setProductDataObject({
      ...productDataObject,
      ...updatedData
    })
  }

  if (translations) {
    setTranslations(translations)
  }

  if (pageReady) {
    return (
      <>
        {hideProduct ? (
          <Container>
            <HtmlContent content={pdpCtaNdr} />
          </Container>
        ) : (
          <>
            {productDataObject &&
              productDataObject.product &&
              productDataObject.product.promoBanner && (
                <ProductBanner image={productDataObject.product.promoBanner} />
              )}
            {productDataObject && productDataObject.product && (
              <ProductDetailView
                translations={translations}
                productData={productDataObject}
                productExtraDataLoaded={
                  productDataObject.product.productExtraDataLoaded || false
                }
                crossSellBanner={crossSellBanner}
                showBar={showbar}
                hideReviews={hidereviews}
                hideRelatedProducts={false}
                hideProduct={hideProduct}
                backgroundColor={backgroundColor}
                canonicalUrl={canonicalUrl}
                updateDataProduct={updateDataProduct}
                updateData={updateData}
                bundleDetailPath={bundleDetailPath}
                line={line}
              />
            )}
          </>
        )}
      </>
    )
  }
}

const ProductDetail = props => {
  return (
    <ToastContext>
      <PublishedComponent
        innerComponent={ProductDetailComponent}
        mainLayout
        {...ComponentDefaultProps}
        {...props}
      />
    </ToastContext>
  )
}

const ComponentTranslations = {
  ...ProductDetailTranslations,
  ...CommonTranslations
}

const ComponentDefaultProps = {
  componentName: 'ProductDetail',
  translations: {
    ...parseTranslations(ComponentTranslations, true)
  },
  productData: {}
}

ProductDetail.getInitialProps = async req => {
  return PublishedComponent.getGlobalInitialProps({
    context: req && req.query && req.query.context,
    line: !!req?.query?.line,
    componentName: ComponentDefaultProps.componentName,
    defaultTranslations: ComponentDefaultProps.translations,
    translationsConfig: ComponentTranslations,
    getSpecificInitialProps: async () => {
      const productid = req.query.productid
      const variantid = req.query.variantid
      const variantIdsToDisplay =
        (req.body.extra && req.body.extra.variantiddisplay) || []
      const line = !!req?.query?.line
      let error = null
      let res = null
      try {
        res = await ProductDetail.getProduct(
          productid,
          variantid,
          req,
          variantIdsToDisplay,
          line
        )
      } catch (err) {
        const message = extractErrorMessage(err)
        // since API GW does not send us a proper 404 we need to treat in a special way this error
        // in order for Drupal to send a proper 404
        if (message === 'Product not available') {
          return {
            error: {
              message,
              status: 404
            }
          }
        } else if (err.data && err.data.data && err.data.data.products) {
          error = {
            message: err.data.message,
            status: err.status
          }
          res = await ProductDetail.getProductInfo(err.data.data)
        } else {
          throw err
        }
      }
      const categoryId = res.categoryId
      const productData = res.product
      const selectedVariant =
        productData.product.options[productData.selectedOptionIndex]
      return {
        error,
        extras: {
          main_category: categoryId || null,
          name: selectedVariant.variantName || null,
          product_id: productid || null,
          seo_title: productData.product.seoTitle,
          seo_description: productData.product.seoDescription
        },
        productData,
        showbar: req.query.showbar === 'true',
        crossSellBanner:
          (req.body.extra && req.body.extra.crossSellBannerPdp) || null,
        hidereviews: (req.body.extra && req.body.extra.hide_reviews) || null,
        backgroundColor:
          (req.body.extra && req.body.extra.background_color) ||
          PublishedComponent.backgrounds.white,
        bundleDetailPath:
          (req.body.extra && req.body.extra.bundle_detail_path) || '',
        canonicalUrl: req.body.extra && req.body.extra.canonicalURL,
        pdpCtaNdr: (req.body.extra && req.body.extra.pdp_cta_ndr) || ''
      }
    }
  })
}

ProductDetail.getProductInfo = async (res, variantIdsToDisplay, variantid) => {
  const requestedProduct = res && res.products && res.products[0]
  const product = mapProductInfoForDisplay(
    requestedProduct,
    variantIdsToDisplay,
    variantid
  )
  return {
    product,
    categoryId: res && res.main_category && res.main_category.category_id
  }
}

ProductDetail.getProduct = async (
  productid,
  variantid,
  req,
  variantIdsToDisplay,
  line
) => {
  const api = new DcxApiManager(line)
  const res = await api.cart.getProducts({
    req,
    params: {
      productid,
      variantid
    }
  })
  return ProductDetail.getProductInfo(res, variantIdsToDisplay, variantid)
}

export default ProductDetail
